import type { MessageKey, Translator } from './translate'

/**
 * The error shape every backend route returns.
 *
 *     { status: 400, error: 'Хэрэглэгч олдсонгүй', code: 'user.not_found' }
 *
 * `code` is the contract; `error` is Mongolian prose kept for logs and for
 * anything that has not learned the codes.
 */
export interface ApiErrorResponse {
  status?: number
  error?: string
  code?: string
  [key: string]: unknown
}

/**
 * Turns an API response into a sentence in the reader's language.
 *
 * An unrecognised code falls back to the generic "something went wrong", **not**
 * to the `error` field the server sent. That prose is Mongolian, and showing it
 * to a Spanish reader is the exact bug this whole mechanism exists to remove —
 * a vague sentence they can read beats a precise one they cannot. The raw
 * message is logged in development so the missing key is easy to spot.
 *
 *     const result = await login(...)
 *     if(result?.error) toast.error(apiError(t, result))
 *
 * @param fallback overrides the generic message, for screens that can say
 *   something more useful about a failure than "something went wrong".
 */
export function apiError(
  t: Translator,
  response: ApiErrorResponse | string | null | undefined,
  fallback: MessageKey = 'common.unknownError',
): string {
  if(!response) return t(fallback)

  const code = typeof response === 'string' ? response : response.code
  if(code) {
    const key = `error.${code}` as MessageKey
    const translated = t(key)
    // createTranslator returns the key itself when it has no entry.
    if(translated !== key) return translated
    if(process.env.NODE_ENV !== 'production') {
      console.warn(`[i18n] untranslated API error code: ${code}`, response)
    }
  }

  return t(fallback)
}

/** True when the response carries this exact error code. */
export function hasErrorCode(response: ApiErrorResponse | null | undefined, code: string): boolean {
  return !!response && response.code === code
}
